import { computed, onBeforeUnmount, watch, type Ref } from 'vue';
import { useRoute } from 'vue-router';
import { clearBreadcrumbTrail, setBreadcrumbTrail } from '@/shared/lib';
import {
  buildFolderBreadcrumbTrail,
  buildModuleBreadcrumbTrail,
  getContainerMatrices,
  isCustomFolderCode,
  isModuleCode,
} from './mapVisibleTreeToNavigation';
import { useVisibleDirectoryTree } from './useVisibleDirectoryTree';
import type { DirectoryContentKind } from './visible-tree.types';

export function useVisibleFolderContext(itemName: Ref<string>) {
  const route = useRoute();
  const { findFolderByCode, findModuleByCode } = useVisibleDirectoryTree();

  const isCustomFolder = computed(() => isCustomFolderCode(itemName.value));
  const isModule = computed(() => isModuleCode(itemName.value));
  const folderEntry = computed(() =>
    itemName.value && !isModule.value ? findFolderByCode(itemName.value) : null
  );
  const moduleNode = computed(() => (isModule.value ? findModuleByCode(itemName.value) : null));
  const customFolderTitle = computed(() => {
    if (isModule.value) return moduleNode.value?.moduleName ?? '';
    return isCustomFolder.value ? (folderEntry.value?.folder.name ?? '') : '';
  });
  const contentKind = computed<DirectoryContentKind | undefined>(
    () => moduleNode.value?.contentKind ?? folderEntry.value?.folder.contentKind
  );
  const matrices = computed(() => {
    if (moduleNode.value) return getContainerMatrices(moduleNode.value);
    return folderEntry.value ? getContainerMatrices(folderEntry.value.folder) : [];
  });

  watch(
    [isCustomFolder, folderEntry, moduleNode],
    ([custom, entry, module]) => {
      if (module) {
        setBreadcrumbTrail(buildModuleBreadcrumbTrail(module, route.path));
      } else if (custom && entry) {
        setBreadcrumbTrail(buildFolderBreadcrumbTrail(entry, route.path));
      } else {
        clearBreadcrumbTrail();
      }
    },
    { immediate: true }
  );

  onBeforeUnmount(clearBreadcrumbTrail);

  return {
    isCustomFolder,
    folderEntry,
    moduleNode,
    customFolderTitle,
    contentKind,
    matrices,
  };
}
